import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/header';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const inputStyle = {
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#333',
    backgroundColor: '#f9f9f9',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'border-color 0.3s ease',
    width: '150px',
};

const formatDateForInput = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ProductionNextDetails = () => {
    const [machines, setMachines] = useState([]);
    const [products, setProducts] = useState([]);
    const [filteredProductionData, setFilteredProductionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [inputDate, setInputDate] = useState('');
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [machinesData, setMachinesData] = useState([]);
    const [formData, setFormData] = useState({
        from_date_time: '',
        to_date_time: '',
        machine_group: '',
        production_date: '',
        interval_type: 'daily'
    });
    const modalRef = useRef();
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editFormData, setEditFormData] = useState({
        production_date: '',
        machine_group: '',
        product: '',
        actual_production: ''
    });
    const [selectedProduction, setSelectedProduction] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
    const deleteModalRef = useRef();

    useEffect(() => {
        const superuserStatus = sessionStorage.getItem('superuser') === 'true';
        setIsSuperuser(superuserStatus);
    }, []);

    const fetchProductionData = async (date) => {
        const token = sessionStorage.getItem('token');
        try {
            const fetchDate = date || inputDate;
            const url = `${BaseURL}meters/production-next/?date=${fetchDate}`;
            const productionResponse = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            
            if (!productionResponse.ok) {
                throw new Error(`HTTP error! status: ${productionResponse.status}`);
            }
            
            const productionData = await productionResponse.json();

            const dataWithIds = productionData.map((item, index) => ({
                ...item,
                id: `${item.Date}-${item.Department}-${index}`
            }));

            setFilteredProductionData(dataWithIds || []);
        } catch (error) {
            console.error('Error fetching production data:', error);
        }
    };

    useEffect(() => {
        const superuserStatus = sessionStorage.getItem('superuser') === 'true';
        setIsSuperuser(superuserStatus);

        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setInputDate(formattedDate);

        const fetchInitialData = async () => {
            try {
                const [machinesResponse, productsResponse] = await Promise.all([
                    fetch(`${BaseURL}meters/machine/`),
                    fetch(`${BaseURL}meters/products/`)
                ]);
                
                const machinesData = await machinesResponse.json();
                const productsData = await productsResponse.json();
                
                setMachines(machinesData);
                setProducts(productsData);
                await fetchProductionData(formattedDate);
                
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);


    const handleDateChange = (e) => {
        const date = e.target.value;
        setInputDate(date);
        fetchProductionData(date);
    };

    const getProductionValue = (department, productName) => {
        const departmentEntries = filteredProductionData.filter(item =>
            item.Department && item.Department.toLowerCase() === department.toLowerCase()
        );

        if (departmentEntries.length === 0) {
            return '-';
        }

        const product = products.find(p => p.product_name === productName);
        if (!product) {
            return '-';
        }

        const key = product.product_key || product.product_name;
        let total = 0;
        let hasValue = false;

        departmentEntries.forEach(entry => {
            const value = entry[key];            
            if (value === "-") {
                return;
            }

            if (value === "0") {
                hasValue = true;
                return;
            }
            if (typeof value === 'string') {
                const [actualStr] = value.split(' / ');
                const actualValue = parseFloat(actualStr);
                if (!isNaN(actualValue)) {
                    total += actualValue;
                    hasValue = true;
                }
            }
            else if (typeof value === 'number') {
                total += value;
                hasValue = true;
            }
        });

        if (!hasValue) {
            return '-';
        }

        const unit = product.weight_unit || '';
        return total === 0 ? `0 ${unit}` : `${total} ${unit}`;
    };

    useEffect(() => {
        axios.get(`${BaseURL}meters/machine/`)
            .then(response => {
                setMachinesData(response.data);
            })
            .catch(error => {
                console.error('Error fetching machine details:', error);
            });
    }, []);

    const filteredMachines = machines.filter(machine =>
        machine.machine_name && machine.machine_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const showAddModal = () => {
        setIsAddModalVisible(true);
    };

    const combineDateWithTime = (date, timeString) => {
        const [hours, minutes, seconds] = timeString.split(':');
        const newDate = new Date(date);
        newDate.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds));
        return newDate;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.machine_group) {
            alert("Please select a department.");
            return;
        }

        if (!formData.from_date_time || !formData.to_date_time || !formData.production_date) {
            alert('Please fill in all date/time fields');
            return;
        }

        const formattedFromDateTime = formData.from_date_time.replace('T', ' ');
        const formattedToDateTime = formData.to_date_time.replace('T', ' ');

        const productionDataToSend = {
            production_date: formData.production_date,
            from_date_time: formattedFromDateTime,
            to_date_time: formattedToDateTime,
            machine_group: formData.machine_group
        };
        const token = sessionStorage.getItem('token');
        axios.post(`${BaseURL}meters/production-next/`, productionDataToSend, {
            headers: {
                'Authorization': `Token ${token}`
            }
        })
            .then(() => {
                setFormData({
                    from_date_time: '',
                    to_date_time: '',
                    machine_group: '',
                    production_date: ''
                });
                setIsAddModalVisible(false);
                setSuccessMessage('Production data added successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
                return fetchProductionData();
            })
            .catch(error => {
                console.error('Error adding production data:', error);
                if (error.response) {
                    if (error.response.status === 400) {
                        if (error.response.data.message === "Already Exist") {
                            alert('Production data for this date and department already exists');
                        } else {
                            alert(error.response.data.error || 'Invalid data. Please check your inputs.');
                        }
                    } else if (error.response.status === 403) {
                        alert("You don't have permission to add production data");
                    } else {
                        alert('An error occurred while adding production data');
                    }
                } else {
                    alert('Network error. Please try again later.');
                }
            })
            .finally(() => {
                setTimeout(() => setSuccessMessage(''), 3000);
            });
    };

    const closeAddModal = () => {
        setIsAddModalVisible(false);
        setFormData({
            from_date_time: '',
            to_date_time: '',
            machineGroup: '',
            production_date: ''
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'machine_group') {
            const selectedMachine = machinesData.find(machine => machine.id.toString() === value);

            if (selectedMachine) {
                const productionDate = formData.production_date ? new Date(formData.production_date) : new Date();
                const fromTime = selectedMachine.from_time || '8:00:00';
                const fromDate = combineDateWithTime(productionDate, fromTime);
                const nextDay = new Date(productionDate);
                nextDay.setDate(nextDay.getDate() + 1);
                const toTime = selectedMachine.to_time || '8:00:00';
                const toDate = combineDateWithTime(nextDay, toTime);

                setFormData(prevData => ({
                    ...prevData,
                    machine_group: value,
                    from_date_time: formatDateForInput(fromDate),
                    to_date_time: formatDateForInput(toDate)
                }));
                return;
            }
        } else if (name === 'production_date') {
            if (formData.machine_group) {
                const selectedMachine = machinesData.find(machine => machine.id.toString() === formData.machine_group);
                if (selectedMachine) {
                    const productionDate = value ? new Date(value) : new Date();
                    const fromTime = selectedMachine.from_time || '8:00:00';
                    const fromDate = combineDateWithTime(productionDate, fromTime);
                    const nextDay = new Date(productionDate);
                    nextDay.setDate(nextDay.getDate() + 1);
                    const toTime = selectedMachine.to_time || '8:00:00';
                    const toDate = combineDateWithTime(nextDay, toTime);

                    setFormData(prevData => ({
                        ...prevData,
                        production_date: value,
                        from_date_time: formatDateForInput(fromDate),
                        to_date_time: formatDateForInput(toDate)
                    }));
                    return;
                }
            }
        }

        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleEditClick = (machine, product) => {
        const machineProductions = filteredProductionData.filter(item => 
            item.Department && item.Department.toLowerCase() === machine.machine_name.toLowerCase()
        );
        const productProduction = machineProductions.find(item => {
            const productKey = product.product_key || product.product_name;
            return item[productKey] !== undefined;
        });
        
        if (productProduction) {
            const productObj = products.find(p => p.product_name === product.product_name);
            
            if (productObj) {
                setSelectedProduction({
                    machine,
                    product: productObj,
                    productionData: productProduction,
                    id: productProduction.id
                });

                const actualProduction = productProduction[product.product_name]?.split(' ')[0] || '';
                
                setEditFormData({
                    production_date: productProduction.Date.split('T')[0],
                    machine_group: machine.id,
                    product: productObj.id,
                    actual_production: actualProduction
                });
                
                setIsEditModalVisible(true);
            }
        } else {
            alert('Production data not found for this product');
        }
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        
        if (!selectedProduction?.id) {
            alert('Production record ID is missing');
            return;
        }

        const token = sessionStorage.getItem('token');
        axios.put(`${BaseURL}meters/production-next/${selectedProduction.id}/`, editFormData, {
            headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json'
            }
        })
        .then(() => {
            setIsEditModalVisible(false);
            setSuccessMessage('Production data updated successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            fetchProductionData();
        })
        .catch(error => {
            console.error('Error updating production data:', error);
            if (error.response) {
                alert(error.response.data.error || 'Failed to update production data');
            } else {
                alert('Network error. Please try again later.');
            }
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(filteredProductionData.map(item => item.id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleCheckboxChange = (id) => {
        setSelectedRows(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const isAllSelected = selectedRows.length === filteredProductionData.length && filteredProductionData.length > 0;

    const handleDelete = () => {
        if (selectedRows.length === 0) {
            alert("Please select at least one record to delete");
            return;
        }
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        const token = sessionStorage.getItem('token');

        if (!token) {
            alert("No authentication token found. Please log in.");
            return;
        }

        try {
            const deleteData = filteredProductionData
                .filter(item => selectedRows.includes(item.id))
                .map(item => ({
                    production_date: item.Date,
                    department: machinesData.find(m => m.machine_name === item.Department)?.id
                }));

            await axios.delete(`${BaseURL}meters/production-next/bulkdelete/`, {
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json'
                },
                data: deleteData
            });

            await fetchProductionData(inputDate);
            setSelectedRows([]);
            setDeleteModalOpen(false);
            setSuccessMessage(
                `Successfully deleted ${selectedRows.length} production record(s)!`
            );
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error("Error deleting rows:", error);
            alert(
                error.response?.data?.message ||
                `Failed to delete records. Please try again.`
            );
            setDeleteModalOpen(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
    }

    return (
        <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{
                backgroundColor: '#007acc',
                color: 'white',
                alignContent: 'center',
                fontSize: '14px',
                height: '40px'
            }}>
                <Header title="PRODUCTION DETAILS" />
            </div>

            <div style={{
                border: '1px solid #ccc',
                padding: '10px',
                marginTop: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                margin: '20px auto',
                maxWidth: '1000px',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '30%' }}>
                        <input
                            type="text"
                            placeholder="Search Department"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                flex: '1',
                                marginRight: '10px',
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{ color: '#007acc', cursor: 'pointer' }}
                        />
                    </div>
                    <div style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <input
                            type="date"
                            value={inputDate}
                            onChange={handleDateChange}
                            style={inputStyle}
                        />
                        {isSuperuser && (
                            <>
                                <button
                                    style={{
                                        padding: '10px 15px',
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    onClick={() => {
                                        if (selectedRows.length > 0) {
                                            handleDelete();
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} style={{ marginRight: '5px' }} />
                                    Delete
                                </button>
                                <button
                                    style={{
                                        padding: '10px 15px',
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    onClick={showAddModal}
                                >
                                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
                                    Add
                                </button>
                            </>
                        )}
                        {/* <button
                            style={{
                                padding: '10px 15px',
                                backgroundColor: '#007acc',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            Download
                        </button> */}
                    </div>
                </div>
            </div>

            <div style={{ textAlign: 'center', margin: '20px 0' }}>
                {successMessage && (
                    <div style={{
                        padding: '10px',
                        borderRadius: '5px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        margin: '10px auto',
                        width: 'fit-content',
                    }}>
                        {successMessage}
                    </div>
                )}
            </div>

            <div style={{
                marginTop: '20px',
                padding: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                maxWidth: '1000px',
                margin: '20px auto',
                marginBottom: '70px',
            }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', minWidth: '800px' }}>
                        <thead style={{ backgroundColor: '#007acc', color: 'white' }}>
                            <tr>
                                {isSuperuser && (
                                <th style={{ padding: '10px' }}>
                                    <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={isAllSelected}
                                    ref={el => {
                                        if (el) {
                                        el.indeterminate = 
                                            selectedRows.length > 0 && 
                                            !isAllSelected;
                                        }
                                    }}
                                    />
                                </th>
                                )}
                                <th style={{ padding: '10px' }}>S.No</th>
                                <th style={{ padding: '10px' }}>Department</th>
                                <th style={{ padding: '10px' }}>Production Date</th>
                                <th style={{ padding: '10px' }}>From Time</th>
                                <th style={{ padding: '10px' }}>To Time</th>
                                {products.map(product => (
                                    <th key={product.id} style={{ padding: '10px' }}>{product.product_name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMachines.map((machine, index) => {
                                const machineProductions = filteredProductionData.filter(item => 
                                    item.Department && item.Department.toLowerCase() === machine.machine_name.toLowerCase()
                                );

                                const productionItem = machineProductions.length > 0 ? machineProductions[0] : { id: `no-data-${machine.id}` };

                                return (
                                    <tr key={machine.id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : '#ffffff' }}>
                                        {isSuperuser && (
                                            <td style={{ padding: '10px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRows.includes(productionItem.id)}
                                                    onChange={() => handleCheckboxChange(productionItem.id)}
                                                />
                                            </td>
                                        )}
                                        <td style={{ padding: '10px' }}>{index + 1}</td>
                                        <td style={{ padding: '10px' }}>{machine.machine_name}</td>
                                        <td style={{ padding: '10px' }}>
                                            {machineProductions.length > 0 ? machineProductions[0].Date.split('T')[0] : '-'}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            {machineProductions.length > 0 ? machineProductions[0].From.split('T')[0] : '-'}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            {machineProductions.length > 0 ? machineProductions[0].To.split('T')[0] : '-'}
                                        </td>
                                        {products.map(product => (
                                            <td 
                                                key={`${machine.id}-${product.id}`} 
                                                style={{ padding: '10px', cursor: 'pointer' }}
                                                onClick={() => handleEditClick(machine, product)}
                                            >
                                                {getProductionValue(machine.machine_name, product.product_name)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {filteredMachines.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            No machines found matching your search
                        </div>
                    )}
                </div>
            </div>
            {isAddModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 999,
                    animation: 'fadeIn 0.3s ease-out',
                }}>
                    <div ref={modalRef} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        width: '650px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        transform: 'scale(1.05)',
                        animation: 'zoomIn 0.4s ease-in-out',
                    }}>
                        <h3 style={{
                            fontSize: '24px',
                            color: '#333',
                            fontFamily: 'Arial, sans-serif',
                            marginBottom: '20px',
                            textAlign: 'center',
                        }}>
                            Add New Production Data
                        </h3>
                        <form>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '20px',
                            }}>
                                <div>
                                    <label style={{
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Production Date</label>
                                    <input
                                        type="date"
                                        name="production_date"
                                        value={formData.production_date}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            boxSizing: 'border-box',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Department</label>
                                    <select
                                        name="machine_group"
                                        value={formData.machine_group}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <option value="">Select a Department</option>
                                        {machinesData.map((machine) => (
                                            <option key={machine.id} value={machine.id}>
                                                {machine.machine_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>From Date Time</label>
                                    <input
                                        type="datetime-local"
                                        name="from_date_time"
                                        value={formData.from_date_time}
                                        onChange={handleChange}
                                        disabled={!isSuperuser}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            boxSizing: 'border-box',
                                            fontSize: '16px',
                                            backgroundColor: isSuperuser ? '#fff' : '#f5f5f5',
                                            cursor: isSuperuser ? 'text' : 'not-allowed',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>To Date Time</label>
                                    <input
                                        type="datetime-local"
                                        name="to_date_time"
                                        value={formData.to_date_time}
                                        onChange={handleChange}
                                        disabled={!isSuperuser}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            boxSizing: 'border-box',
                                            fontSize: '16px',
                                            backgroundColor: isSuperuser ? '#fff' : '#f5f5f5',
                                            cursor: isSuperuser ? 'text' : 'not-allowed',
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{
                                marginTop: '20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}>
                                <button
                                    onClick={handleSubmit}
                                    style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        transition: 'background-color 0.3s',
                                        width: '20%',
                                        marginLeft: '210px'
                                    }}
                                >
                                    Save
                                </button>
                                <button
                                    onClick={closeAddModal}
                                    style={{
                                        backgroundColor: '#ff4d4d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        transition: 'background-color 0.3s',
                                        width: '20%',
                                        marginRight: '150px'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#e04040'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#ff4d4d'}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isEditModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 999,
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        width: '400px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}>
                        <div style={{ 
                            textAlign: 'center',
                            marginBottom: '20px',
                            paddingBottom: '15px',
                            borderBottom: '1px solid #eee'
                        }}>
                            <h3 style={{
                                fontSize: '24px',
                                color: '#333',
                                marginBottom: '5px',
                            }}>
                                Edit Production Data
                            </h3>
                            <div style={{ 
                                fontSize: '16px',
                                color: '#555',
                                marginBottom: '5px'
                            }}>
                                <strong>Department:</strong> {selectedProduction?.machine.machine_name || 'N/A'}
                            </div>
                            <div style={{ 
                                fontSize: '16px',
                                color: '#555'
                            }}>
                                <strong>Product:</strong> {selectedProduction?.product.product_name || 'N/A'}
                            </div>
                        </div>

                        <form>
                            <input
                                type="hidden"
                                name="production_date"
                                value={editFormData.production_date}
                            />
                            <input
                                type="hidden"
                                name="machine_group"
                                value={editFormData.machine_group}
                            />
                            <input
                                type="hidden"
                                name="product"
                                value={editFormData.product}
                            />

                            <div style={{ marginBottom: '25px' }}>
                                <label style={{
                                    display: 'block',
                                    fontWeight: '600',
                                    color: '#444',
                                    marginBottom: '8px',
                                    fontSize: '16px',
                                }}>
                                    Actual Production ({selectedProduction?.product.weight_unit || 'units'})
                                </label>
                                <input
                                    type="number"
                                    name="actual_production"
                                    value={editFormData.actual_production}
                                    onChange={(e) => setEditFormData({ ...editFormData, actual_production: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        fontSize: '16px',
                                    }}
                                    step="0.01"
                                    min="0"
                                    autoFocus
                                />
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '15px',
                            }}>
                                <button
                                    type="button"
                                    onClick={handleEditSubmit}
                                    style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '48%',
                                    }}
                                >
                                    Update
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalVisible(false)}
                                    style={{
                                        backgroundColor: '#ff4d4d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '48%',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isDeleteModalOpen && (
                <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                }}
                >
                <div ref={deleteModalRef}
                    style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    width: "400px",
                    textAlign: "center",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    }}
                >
                    <h3>Confirm Deletion</h3>
                    <p>Are you sure you want to delete the selected ProductionDetails?</p>
                    <div
                    style={{
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-around",
                    }}
                    >
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                        style={{
                            padding: "10px 15px",
                            color: "white",
                            backgroundColor: "#4CAF50",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                        onClick={confirmDelete}
                        >
                        Yes, Delete
                        </button>
                        <button
                        style={{
                            padding: "10px 15px",
                            color: "white",
                            backgroundColor: "#f44336",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                        onClick={() => setDeleteModalOpen(false)}
                        >
                        Cancel
                        </button>
                    </div>
                    </div>
                </div>
                </div>
            )}
        </div>
    );
};

export default ProductionNextDetails;